import axios from 'axios'
import { addDays, format, startOfToday } from 'date-fns'
import { ICalendarDate, ICalendarEvent, ICalendarEventType } from 'interface/calendar'
import { ICoordinates } from 'interface/map'
import aroAPI from './config'
import { Ioffice } from 'interface'
import { IEmployee } from 'interface/employee'
import { ICalendarEventParticipant } from 'interface/calendar'

const fetchCurrentWeekCalendarEventsWithWeatherForecasts = async (office: Ioffice): Promise<{
  data: Array<ICalendarDate>,
  error: string | null,
}> => {
  const currentDate = startOfToday()

  const currentWeekEventsResponse = await fetchCalendarEvents({
    office_id: office.id,
    start_date: format(currentDate, 'yyyy-MM-dd'),
    end_date: format(addDays(currentDate, 7), 'yyyy-MM-dd'),
  })

  let error = null

  let currentWeekWeather: Array<{
    temperature: number
    shortForecast: string
    icon: string
  }> | null = null
  if (office.location) {
    try {
      currentWeekWeather = await fetchCurrentWeekWeather({
        coordinates: {
          lat: office.location.lat,
          lng: office.location.lng
        }
      })
    } catch (e) {
      error = `Error fetching weather forecast data for ${office.name}`
    }
  }

  const data: Array<ICalendarDate> = []
  for (let i = 0; i < 7; i++) {
    const date = addDays(currentDate, i)
    data.push({
      date,
      name: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : format(date, 'EEEE'),
      events: currentWeekEventsResponse.result.events[format(date, 'yyyy-MM-dd')] || [],
      weather: currentWeekWeather ? {
        icon: currentWeekWeather[i * 2].icon,
        forecast: currentWeekWeather[i * 2].shortForecast,
        temperature: {
          day: currentWeekWeather[i * 2].temperature,
          night: currentWeekWeather[i * 2 + 1].temperature,
        }
      } : null
    })
  }

  return { data, error }
}

const fetchCalendarEvents = async (params: {
  office_id: number
  start_date?: string // 2024-01-01
  end_date?: string // 2024-03-01
  page?: number
  per_page?: number
  search_text?: string
}) : Promise<{
  _metadata: {
    success: boolean
    pagination: {
      total: number
      per_page: number
      current_page: number
      total_pages: number
    }
  }
  result: {
    events: Record<string, ICalendarEvent[]>
  }
}> => {
  const response = await aroAPI.get('/calendar/events', {
    params
  })
  return response.data
}

const fetchCurrentWeekWeather = async ({
  coordinates,
}: {
  coordinates: ICoordinates,
}): Promise<Array<{
  icon: string
  temperature: number
  shortForecast: string
}>> => {
  const weatherPointResponse = await axios.get<{
    properties: {
      forecast: string
    }
  }>(`https://api.weather.gov/points/${coordinates.lat},${coordinates.lng}`)

  const weatherGridPointResponse =  await axios.get<{
    properties: {
      periods: Array<{
        icon: string
        temperature: number
        shortForecast: string
      }>
    }
  }>(weatherPointResponse.data.properties.forecast)

  return weatherGridPointResponse.data.properties.periods
}

const createCalendarEvent = async (params: {
  office_id: number
  title: string
  description: string
  event_type: string
  start_date: string // example: 2024-01-01
  end_date: string | null // example: 2024-04-30
  start_at: string // example: 08:00:00
  end_at: string // example: 08:30:00
  interval: string
  week_days?: string[] | null // example: [monday]
  location_lat: number
  location_lng: number
  address: string
  city: string
  state: string
  zip: string
  end_after: string
  occurrences: number
  repeat_every: number
  week_num: null | number
}) : Promise<{
  _metadata: {
    success: boolean
  }
  result: {
    message: string
    id?: number
    errors?: {
      title: Array<string>
    }
  }
}> => {
  try {
    const response = await aroAPI.post('/calendar/events', params)
    return response.data
  } catch (exception: any) {
    if (exception.response?.data) {
      return exception.response.data
    }
    throw exception
  }
}

const fetchCalendarEventTypes = async (): Promise<ICalendarEventType[]> => {
  const response = await aroAPI.get('/calendar/event-types')
  return response.data.result
}

const fetchUserAvatarResponseCache: Record<string, {
  _metadata: {
    success: boolean
  }
  result: {
    avatarBase64: string
  }
}> = {}

const fetchUserAvatar = async (user_id: string, useCache?: boolean): Promise<{
  _metadata: {
    success: boolean
  }
  result: {
    avatarBase64: string
  }
}> => {
  if (useCache && fetchUserAvatarResponseCache[user_id]) {
    return fetchUserAvatarResponseCache[user_id]
  }
  try {
    const response = await aroAPI.get(`/calendar/employees/${user_id}/avatar`)
    fetchUserAvatarResponseCache[user_id] = response.data
    return response.data
  } catch (exception: any) {
    if (exception.response?.data) {
      fetchUserAvatarResponseCache[user_id] = exception.response.data
      return exception.response.data
    }
    throw exception
  }
}

const fetchOfficeEmployees = async (officeId: number): Promise<IEmployee[]> => {
  const response = await aroAPI.get(`/calendar/office/${officeId}/employees`)
  return response.data.result.employees
}

const addCalendarEventParticipants = async (eventId: number, participantIds: number[]): Promise<{
  _metadata: {
    success: boolean
  },
  result: {
    message: string
  }
}> => {
  const response = await aroAPI.put(`/calendar/events/${eventId}/participants`, {
    participant_ids: participantIds
  })
  return response.data
}

const fetchOfficeEmployeesWithAvatars = async (officeId: number): Promise<IEmployee[]> => {
  const employees = await fetchOfficeEmployees(officeId)
  for (const employee of employees) {
    try {
      const avatarResponse = await fetchUserAvatar(employee.external_id)
      employee.avatar = avatarResponse.result.avatarBase64
    } catch (e) {
      // ignore this case, mostly 404 - no avatar available for this employee 
    }
  }
  return employees
}

const fetchCalendarEventParticipants = async (eventId: number): Promise<ICalendarEventParticipant[]> => {
  const response = await aroAPI.get(`/calendar/events/${eventId}/participants`)
  return response.data.result.participants.filter((item: ICalendarEventParticipant) => item.is_invited)
}

const deleteCalendarEvent = async (eventId: number): Promise<{
  _metadata: {
    success: boolean
  },
  result: {
    message: string
  }
}> => {
  const response = await aroAPI.delete(`/calendar/events/${eventId}`)
  return response.data
}

const updateCalendarEvent = async (params: {
  eventId: number
  title: string
  description: string
  location_lat: number
  location_lng: number
  address: string
  city: string
  state: string
  zip: string
}): Promise<{
  _metadata: {
    success: boolean
  },
  result: {
    message: string
    id: number
    errors?: {
      title: Array<string>
    }
  }
}> => {
  const response = await aroAPI.patch(`/calendar/events/${params.eventId}`, params)
  return response.data
}

const overrideCalendarEvent = async (params: {
  eventId: number
  date: string // example: 2024-01-01
  start_time: string // example: 08:00:00
  end_time: string // example: 08:30:00
  title: string
  description: string
  is_canceled: boolean
  location_lat: number
  location_lng: number
  meeting_link: string
  address: string
  city: string
  state: string
  zip: string
}): Promise<{
  _metadata: {
    success: boolean
  },
  result: {
    message: string
    id: number
    errors?: {
      title: Array<string>
    }
  }
}> => {
  const response = await aroAPI.put(`/calendar/events/${params.eventId}/overrides`, params)
  return response.data
}

export {
  fetchCurrentWeekCalendarEventsWithWeatherForecasts,
  fetchCalendarEvents,
  fetchCalendarEventTypes,
  createCalendarEvent,
  fetchUserAvatar,
  fetchOfficeEmployees,
  fetchOfficeEmployeesWithAvatars,
  addCalendarEventParticipants,
  fetchCalendarEventParticipants,
  deleteCalendarEvent,
  updateCalendarEvent,
  overrideCalendarEvent
}
